import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { API_BASE_URL } from '../services/api';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [inspectionData, setInspectionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingInspections, setLoadingInspections] = useState(false);
  const [error, setError] = useState('');
  const [inspectionError, setInspectionError] = useState('');
  
  // Date filter states
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1); // Default to last month
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });

  useEffect(() => {
    fetchDashboardData();
    fetchInspectionData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/user/home`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok) {
        setDashboardData(data.data);
      } else {
        setError(data.message || 'Failed to fetch dashboard data');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchInspectionData = async () => {
    setLoadingInspections(true);
    setInspectionError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/project/list/inspection?start=${startDate}&end=${endDate}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok) {
        setInspectionData(data.data || []);
      } else {
        setInspectionError(data.message || 'Failed to fetch inspection data');
      }
    } catch (err) {
      setInspectionError('Network error. Please try again.');
    } finally {
      setLoadingInspections(false);
    }
  };

  const downloadCSV = () => {
    if (!inspectionData || inspectionData.length === 0) {
      alert('No data to download');
      return;
    }

    // Get all unique keys from all objects to create comprehensive headers
    const allKeys = new Set();
    inspectionData.forEach(item => {
      Object.keys(item).forEach(key => allKeys.add(key));
    });
    
    const headers = Array.from(allKeys);
    
    // Convert each object to CSV row using all possible headers
    const csvData = inspectionData.map(inspection => 
      headers.map(header => {
        const value = inspection[header];
        // Handle nested objects/arrays by converting to string
        if (typeof value === 'object' && value !== null) {
          return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
        }
        // Handle null/undefined
        if (value === null || value === undefined) {
          return '';
        }
        // Handle strings with quotes/commas
        return `"${String(value).replace(/"/g, '""')}"`;
      })
    );

    // Combine headers and data
    const csvContent = [
      headers.map(header => `"${header}"`).join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inspections_raw_${startDate}_to_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status) => {
    // Define color schemes for different status patterns
    const getColorForStatus = (status) => {
      const lowerStatus = status.toLowerCase();
      
      if (lowerStatus.includes('completed') || lowerStatus.includes('whitewash')) {
        return { bg: 'bg-green-100', text: 'text-green-800' };
      }
      if (lowerStatus.includes('roof') || lowerStatus.includes('plastering')) {
        return { bg: 'bg-blue-100', text: 'text-blue-800' };
      }
      if (lowerStatus.includes('lintel') || lowerStatus.includes('lintal')) {
        return { bg: 'bg-yellow-100', text: 'text-yellow-800' };
      }
      if (lowerStatus.includes('basement') || lowerStatus.includes('fc laid')) {
        return { bg: 'bg-orange-100', text: 'text-orange-800' };
      }
      if (lowerStatus.includes('marked') || lowerStatus.includes('earthwork')) {
        return { bg: 'bg-purple-100', text: 'text-purple-800' };
      }
      
      return { bg: 'bg-gray-100', text: 'text-gray-800' };
    };
    
    const statusStyle = getColorForStatus(status || 'Unknown');
    
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
        {status || 'Unknown'}
      </span>
    );
  };

  // Extract all unique statuses from the API response
  const getAllUniqueStatuses = () => {
    if (!dashboardData?.status) return [];
    
    const statusSet = new Set();
    
    // Iterate through all blocks
    Object.values(dashboardData.status).forEach(blockStatuses => {
      // Add each status from this block to the set
      Object.keys(blockStatuses).forEach(status => {
        statusSet.add(status);
      });
    });
    
    // Convert Set to Array and filter out "Not Started"
    return Array.from(statusSet).filter(status => status !== 'Not Started');
  };

  // Generate dynamic color mapping
  const generateStatusColors = (statuses) => {
    // Define a color palette
    const colorPalette = [
      '#EF4444', // Red
      '#F97316', // Orange
      '#F59E0B', // Amber
      '#EAB308', // Yellow
      '#84CC16', // Lime
      '#22C55E', // Green
      '#10B981', // Emerald
      '#14B8A6', // Teal
      '#06B6D4', // Cyan
      '#0EA5E9', // Sky
      '#3B82F6', // Blue
      '#6366F1', // Indigo
      '#8B5CF6', // Violet
      '#A855F7', // Purple
    ];
    
    const colorMap = {};
    statuses.forEach((status, index) => {
      colorMap[status] = colorPalette[index % colorPalette.length];
    });
    
    return colorMap;
  };
const getBlockStatusData = () => {
  if (!dashboardData?.status) return [];
  
  const statusData = dashboardData.status;
  const blockData = [];
  
  Object.entries(statusData).forEach(([blockName, statuses]) => {
    // Calculate total houses from sum of ALL status counts
    const totalHousesInBlock = Object.values(statuses)
      .reduce((sum, count) => sum + count, 0);
    
    if (totalHousesInBlock === 0) return;
    
    const blockEntry = { 
      block: blockName, 
      totalHouses: totalHousesInBlock 
    };
    
    // Add all statuses dynamically
    Object.entries(statuses).forEach(([statusName, count]) => {
      blockEntry[statusName] = count;
    });
    
    blockData.push(blockEntry);
  });
  
  return blockData;
};
  const getTotalHouses = () => {
    return dashboardData?.total || 0;
  };

  const getCompletedCount = () => {
    return dashboardData?.completed || 0;
  };

  const getInProgressCount = () => {
    return dashboardData?.progress || 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getInspectionDate = (inspection) => {
    // Try inspect field first, then created_at, then updated_at
    return inspection.inspect || inspection.created_at || inspection.updated_at;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl font-semibold mb-4">Error</div>
          <p className="text-gray-600">{error}</p>
          <button 
            onClick={fetchDashboardData}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const blockStatusData = getBlockStatusData();
  const totalHouses = getTotalHouses();
  const completedHouses = getCompletedCount();
  const inProgressHouses = getInProgressCount();
  const uniqueStatuses = getAllUniqueStatuses();
  const statusColors = generateStatusColors(uniqueStatuses);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="w-full px-2 sm:px-4 py-4">

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Houses</p>
                <p className="text-2xl font-bold text-gray-900">{totalHouses}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-gray-900">{completedHouses}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-gray-900">{inProgressHouses}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Blocks</p>
                <p className="text-2xl font-bold text-gray-900">{Object.keys(dashboardData?.area || {}).length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Full Width Stacked Bar Chart */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Construction Progress by Blocks</h3>
          <div style={{ height: '500px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={blockStatusData} 
                margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="block" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                  fontSize={12}
                  tick={{ fontSize: 11 }}
                />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [value, name]}
                  labelFormatter={(label) => {
                    const blockData = blockStatusData.find(item => item.block === label);
                    return `${label} (Total: ${blockData?.totalHouses || 0} houses)`;
                  }}
                  labelStyle={{ color: '#374151' }}
                />
                <Legend />
                
                {/* Dynamically render Bar components */}
                {uniqueStatuses.map(status => (
                  <Bar 
                    key={status}
                    dataKey={status} 
                    stackId="a" 
                    fill={statusColors[status]} 
                    name={status} 
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Inspections Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xl font-bold text-gray-900 mb-4 sm:mb-0">Recent Inspections</h2>
              
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                {/* Date Filter */}
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">From:</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">To:</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <button
                    onClick={fetchInspectionData}
                    disabled={loadingInspections}
                    className="bg-blue-600 text-white px-4 py-1 rounded-md text-sm hover:bg-blue-700 transition duration-200 disabled:opacity-50"
                  >
                    {loadingInspections ? 'Loading...' : 'Apply Filter'}
                  </button>
                </div>
                
                {/* Download CSV Button */}
                <button
                  onClick={downloadCSV}
                  disabled={!inspectionData || inspectionData.length === 0}
                  className="bg-green-600 text-white px-4 py-1 rounded-md text-sm hover:bg-green-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Download CSV
                </button>
              </div>
            </div>
          </div>

          {/* Inspection Error */}
          {inspectionError && (
            <div className="px-6 py-4 bg-red-50 border-b border-red-200">
              <p className="text-red-600 text-sm">{inspectionError}</p>
            </div>
          )}

          {/* Loading State */}
          {loadingInspections && (
            <div className="px-6 py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading inspections...</p>
            </div>
          )}

          {/* Inspection Table */}
          {!loadingInspections && !inspectionError && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Housing ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beneficiary</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Block</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Panchayat</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inspection Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inspectionData.length > 0 ? (
                    inspectionData.map((inspection, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {inspection.h_id || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {inspection.beneficiary || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {inspection.block || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {inspection.panchayat || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(
                            inspection.progress?.stage ||
                            inspection.project_stage ||
                            'Unknown'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(getInspectionDate(inspection))}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                        No inspections found for the selected date range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;